/**
 * Safetensors header parsing — pure functions over bytes, no I/O.
 *
 * The format is what makes this whole product possible without a backend:
 *
 *   [0, 8)        u64 little-endian: N, the length of the JSON header
 *   [8, 8+N)      the JSON header — one entry per tensor, each carrying dtype,
 *                 shape, and a [start, end) byte range
 *   [8+N, EOF)    the tensor payloads, at those offsets *relative to 8+N*
 *
 * So two small Range requests — eight bytes, then N bytes — yield a complete
 * inventory of a multi-gigabyte file, and any single tensor is one more Range
 * request at a known offset. Nothing here downloads a model.
 *
 * Reference: https://huggingface.co/docs/safetensors/en/metadata_parsing
 */

/** Bytes of little-endian u64 prefix carrying the JSON header's length. */
export const HEADER_LENGTH_PREFIX_BYTES = 8;

/**
 * Refuse to allocate for an implausible header length. A corrupt or non-safetensors
 * file read as one yields a garbage u64 — often near 2^64 — and without this the
 * first thing we'd do is ask the Hub for a Range of that size. 100 MB is far
 * above any real header (the largest observed are single-digit MB on models with
 * hundreds of thousands of tensors) and far below anything that could hurt.
 */
export const MAX_HEADER_BYTES = 100 * 1024 * 1024;

/**
 * Bytes per element, by safetensors dtype string.
 *
 * The float8 and 4-bit variants are listed because they appear in real repos and
 * we must size their byte ranges correctly to *list* them — decoding them to
 * float is a separate concern (and, for the packed 4-bit forms, not a plain
 * element-size question at all; see the quantization work in the plan's v1.5).
 */
const DTYPE_BYTES: Readonly<Record<string, number>> = {
  BOOL: 1,
  U8: 1,
  I8: 1,
  F8_E5M2: 1,
  F8_E4M3: 1,
  I16: 2,
  U16: 2,
  F16: 2,
  BF16: 2,
  I32: 4,
  U32: 4,
  F32: 4,
  I64: 8,
  U64: 8,
  F64: 8,
};

export function dtypeByteWidth(dtype: string): number | undefined {
  return DTYPE_BYTES[dtype];
}

/**
 * Every safetensors dtype this app can size, as a list.
 *
 * Exported so the educational layer's coverage test has an authoritative set to
 * check against rather than a second copy of these names. A dtype added above
 * and not explained then fails that test, which is the point of it: the copy
 * cannot silently fall behind the formats.
 */
export const KNOWN_SAFETENSORS_DTYPES: readonly string[] = Object.keys(DTYPE_BYTES);

/** One tensor's entry in the header, normalized. */
export interface TensorEntry {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  /** Byte range within the payload region, i.e. relative to `dataStart`. */
  readonly dataOffsets: readonly [start: number, end: number];
  /** Product of `shape`; 1 for a scalar (empty shape), per the format. */
  readonly numel: number;
  readonly byteLength: number;
}

export interface ParsedHeader {
  readonly tensors: readonly TensorEntry[];
  /** The `__metadata__` object, if the file carried one. */
  readonly metadata: Readonly<Record<string, string>> | undefined;
  /** Absolute file offset where payloads begin: 8 + headerLength. */
  readonly dataStart: number;
  readonly headerLength: number;
}

/**
 * Read the JSON header's length from the file's first eight bytes.
 *
 * Returns a `number`, not a `bigint`: the value is bounded by MAX_HEADER_BYTES
 * immediately, so it is always far inside the safe-integer range by the time it
 * escapes this function.
 */
export function parseHeaderLength(firstEightBytes: ArrayBuffer): number {
  if (firstEightBytes.byteLength < HEADER_LENGTH_PREFIX_BYTES) {
    throw new Error(
      `Safetensors header length prefix truncated: got ${firstEightBytes.byteLength} bytes, need ${HEADER_LENGTH_PREFIX_BYTES}`,
    );
  }
  const length = new DataView(firstEightBytes).getBigUint64(0, true);
  if (length === 0n) {
    throw new Error("Safetensors header length is zero — not a safetensors file");
  }
  if (length > BigInt(MAX_HEADER_BYTES)) {
    throw new Error(
      `Safetensors header claims ${length} bytes, above the ${MAX_HEADER_BYTES}-byte sanity cap — refusing to fetch it. This is usually a sign the file is not safetensors.`,
    );
  }
  return Number(length);
}

/**
 * Parse the JSON header body into normalized entries.
 *
 * `headerLength` is passed in (rather than recomputed from `json.length`) because
 * the JSON is UTF-8 bytes on the wire and its decoded string length is not its
 * byte length — `dataStart` must be derived from the byte count or every offset
 * downstream is wrong for any file whose header contains a non-ASCII tensor name.
 *
 * Entries come back sorted by their start offset, which is both the order they
 * sit in the file and the order that makes a "where do the bytes live" ledger
 * readable.
 */
export function parseHeaderJson(json: string, headerLength: number): ParsedHeader {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (cause) {
    throw new Error("Safetensors header is not valid JSON", { cause });
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("Safetensors header must be a JSON object");
  }

  const entries: TensorEntry[] = [];
  let metadata: Record<string, string> | undefined;

  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (name === "__metadata__") {
      // Free-form string map the format reserves for producers; not a tensor.
      if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        metadata = value as Record<string, string>;
      }
      continue;
    }
    entries.push(normalizeEntry(name, value));
  }

  entries.sort((a, b) => a.dataOffsets[0] - b.dataOffsets[0]);

  return {
    tensors: entries,
    metadata,
    dataStart: HEADER_LENGTH_PREFIX_BYTES + headerLength,
    headerLength,
  };
}

function normalizeEntry(name: string, value: unknown): TensorEntry {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Tensor "${name}": header entry is not an object`);
  }
  const entry = value as Record<string, unknown>;

  const dtype = entry.dtype;
  if (typeof dtype !== "string") {
    throw new Error(`Tensor "${name}": missing or non-string dtype`);
  }

  const shapeRaw = entry.shape;
  if (
    !Array.isArray(shapeRaw) ||
    !shapeRaw.every((d) => Number.isInteger(d) && d >= 0)
  ) {
    throw new Error(
      `Tensor "${name}": shape must be an array of non-negative integers`,
    );
  }
  const shape = shapeRaw as number[];

  const offsetsRaw = entry.data_offsets;
  if (
    !Array.isArray(offsetsRaw) ||
    offsetsRaw.length !== 2 ||
    !offsetsRaw.every((n) => Number.isInteger(n) && n >= 0)
  ) {
    throw new Error(
      `Tensor "${name}": data_offsets must be a [start, end] integer pair`,
    );
  }
  const [start, end] = offsetsRaw as [number, number];
  if (end < start) {
    throw new Error(
      `Tensor "${name}": data_offsets end (${end}) precedes start (${start})`,
    );
  }

  // An empty shape is a scalar — one element, not zero. `reduce` with an initial
  // value of 1 gets this right for free; writing it as a loop over shape[0] would not.
  const numel = shape.reduce((a, b) => a * b, 1);
  const byteLength = end - start;

  // Cross-check the declared range against what shape × dtype implies. This is
  // the cheapest possible integrity check on a remote file and it costs one
  // multiplication, so it always runs — a mismatch means we would read the wrong
  // bytes, which is far worse than refusing to read at all. Unknown dtypes skip
  // the check rather than fail it: a dtype we don't recognize is a gap in
  // DTYPE_BYTES, not evidence the file is bad.
  //
  // NOTE — we are deliberately stricter than `@huggingface/hub`, which permits a
  // span larger than the implied size, citing legitimate padding. The authority
  // is the reference `safetensors` implementation (Rust), which requires exact
  // `numel × width` spans; the JS library's tolerance is the outlier, not this
  // check. Ruled 2026-08-07: stay strict in both directions, no speculative
  // clamping. Tolerating an over-long span would mean picking which bytes to
  // read out of a header we have already proven inconsistent — "reading the
  // wrong bytes" with extra confidence. If a real padded repo ever turns up,
  // bring the file and we revisit against the spec with evidence.
  const width = dtypeByteWidth(dtype);
  if (width !== undefined && numel * width !== byteLength) {
    const expected = numel * width;
    throw new Error(
      `Tensor "${name}": header is inconsistent and this file is refused. Shape ${shape.join("×")} of ${dtype} implies ${expected} bytes (${numel} elements × ${width}), but data_offsets [${start}, ${end}) span ${byteLength} — a difference of ${byteLength - expected}. Reading it would mean choosing which bytes to trust from a header already known to be wrong. If this is a real repo, please report it with the model id: the file may use a layout we don't handle yet.`,
    );
  }

  return { name, dtype, shape, dataOffsets: [start, end], numel, byteLength };
}

/**
 * The sharded-model index (`model.safetensors.index.json`): a map from tensor
 * name to the shard file that holds it. Sharded repos have no single header —
 * every shard carries its own, and this file is the only thing that says which
 * shards exist.
 */
export interface ShardIndex {
  readonly weightMap: Readonly<Record<string, string>>;
  /** Distinct shard filenames, in first-seen order. */
  readonly shards: readonly string[];
  readonly totalSize: number | undefined;
}

export function parseShardIndex(json: string): ShardIndex {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (cause) {
    throw new Error("Shard index is not valid JSON", { cause });
  }
  if (raw === null || typeof raw !== "object") {
    throw new Error("Shard index must be a JSON object");
  }

  const weightMapRaw = (raw as Record<string, unknown>).weight_map;
  if (
    weightMapRaw === null ||
    typeof weightMapRaw !== "object" ||
    Array.isArray(weightMapRaw)
  ) {
    throw new Error("Shard index is missing its weight_map object");
  }
  const weightMap = weightMapRaw as Record<string, string>;

  const shards: string[] = [];
  for (const file of Object.values(weightMap)) {
    if (typeof file !== "string") {
      throw new Error("Shard index weight_map values must be shard filenames");
    }
    if (!shards.includes(file)) shards.push(file);
  }

  const metadata = (raw as Record<string, unknown>).metadata;
  const totalSizeRaw =
    metadata !== null && typeof metadata === "object"
      ? (metadata as Record<string, unknown>).total_size
      : undefined;

  return {
    weightMap,
    shards,
    totalSize: typeof totalSizeRaw === "number" ? totalSizeRaw : undefined,
  };
}
